import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api';
import { Users, Eye, TrendingUp, TrendingDown, Activity, RefreshCw } from 'lucide-react';

const WINDOW_META = { '7d': '7 days', '15d': '15 days', '30d': '30 days' };

const AnalyticsSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData(await adminApi.analytics()); } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // refresh active count every 15s
    return () => clearInterval(t);
  }, []);

  if (loading && !data) {
    return <div className="bg-white rounded-3xl crunch-shadow p-10 text-center font-brico opacity-70">Loading…</div>;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl crunch-shadow-sm p-4 md:p-5 bg-[#D8F0EC]">
          <div className="text-xs font-bold uppercase tracking-widest opacity-80 flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#2A9D8F] animate-pulse" /> Active Now
          </div>
          <div className="font-display text-4xl md:text-5xl mt-2">{data.active_now}</div>
          <div className="text-[10px] font-brico opacity-60 mt-1">Live in the last 90s</div>
        </div>
        {['7d', '15d', '30d'].map((k) => {
          const w = data.windows[k];
          const positive = w.growth_pct != null && w.growth_pct >= 0;
          return (
            <div key={k} className="rounded-2xl crunch-shadow-sm p-4 md:p-5 bg-white">
              <div className="text-xs font-bold uppercase tracking-widest opacity-80 flex items-center gap-1">
                <Users size={14} /> Last {WINDOW_META[k]}
              </div>
              <div className="mt-2 flex items-end gap-3">
                <div>
                  <div className="font-display text-3xl md:text-4xl leading-none">{w.visits}</div>
                  <div className="text-[10px] font-brico uppercase tracking-widest opacity-60 mt-1">unique</div>
                </div>
                <div className="text-black/20 text-3xl leading-none pb-1">·</div>
                <div>
                  <div className="font-display text-2xl md:text-3xl leading-none opacity-70">{w.pageviews ?? 0}</div>
                  <div className="text-[10px] font-brico uppercase tracking-widest opacity-60 mt-1">pageviews</div>
                </div>
              </div>
              <div className={`text-xs font-brico font-bold mt-2 flex items-center gap-1 ${positive ? 'text-[#2A9D8F]' : 'text-[#E63946]'}`}>
                {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {w.growth_pct == null ? '—' : `${positive ? '+' : ''}${w.growth_pct}% unique vs prev`}
              </div>
            </div>
          );
        })}
      </div>

      <ChartCard series={data.series} />

      <div className="bg-white rounded-3xl crunch-shadow p-5">
        <div className="flex items-center gap-2 mb-3">
          <Eye size={16} className="text-[#E63946]" />
          <div className="font-display text-lg uppercase">Top Pages (30d)</div>
        </div>
        {data.top_paths.length === 0 ? (
          <div className="text-sm font-brico opacity-70 py-4 text-center">No pageviews yet.</div>
        ) : (
          <div className="space-y-1">
            {data.top_paths.map((p, i) => (
              <div key={i} className="flex items-center justify-between border-b border-black/5 last:border-b-0 py-2 font-brico">
                <div className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-[#FFF4E6] text-[10px] font-bold flex items-center justify-center">#{i + 1}</span> {p.path}</div>
                <div className="font-bold">{p.views} views</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ChartCard = ({ series }) => {
  const W = 780, H = 220, PAD_L = 40, PAD_R = 12, PAD_T = 14, PAD_B = 32;
  const cw = W - PAD_L - PAD_R, ch = H - PAD_T - PAD_B;
  const maxU = Math.max(1, ...series.map((s) => s.unique_visitors));
  const maxP = Math.max(1, ...series.map((s) => s.pageviews || 0));
  const maxV = Math.max(maxU, maxP);
  const barW = series.length ? Math.max(3, Math.floor((cw / series.length) / 2) - 2) : 0;
  const labelFmt = (s) => new Date(s + 'T00:00:00Z').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

  return (
    <div className="bg-white rounded-3xl crunch-shadow p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-[#E63946]" />
          <div className="font-display text-lg uppercase">Traffic · Last 30 days</div>
        </div>
        <div className="flex items-center gap-4 text-xs font-brico">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#2A9D8F]" /> Unique visitors</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#E63946] opacity-70" /> Pageviews</span>
        </div>
      </div>
      {series.length === 0 ? (
        <div className="text-center font-brico opacity-70 py-10">No visits yet. Once shoppers come in, this chart will fill up.</div>
      ) : (
        <div className="w-full overflow-x-auto">
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet">
            {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
              const y = PAD_T + ch - f * ch;
              return <line key={i} x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#00000012" />;
            })}
            <text x={PAD_L - 6} y={PAD_T + 4} textAnchor="end" fontSize="10" fill="#00000080">{maxV}</text>
            <text x={PAD_L - 6} y={PAD_T + ch + 4} textAnchor="end" fontSize="10" fill="#00000080">0</text>
            {series.map((s, i) => {
              const slot = cw / series.length;
              const cx = PAD_L + i * slot + slot / 2;
              const hu = (s.unique_visitors / maxV) * ch;
              const hp = ((s.pageviews || 0) / maxV) * ch;
              return (
                <g key={i}>
                  <rect x={cx - barW - 1} y={PAD_T + ch - hu} width={barW} height={hu} rx="2" fill="#2A9D8F">
                    <title>{`${labelFmt(s.date)} · ${s.unique_visitors} unique`}</title>
                  </rect>
                  <rect x={cx + 1} y={PAD_T + ch - hp} width={barW} height={hp} rx="2" fill="#E63946" opacity="0.75">
                    <title>{`${labelFmt(s.date)} · ${s.pageviews || 0} pageviews`}</title>
                  </rect>
                  {(series.length <= 15 || i % Math.ceil(series.length / 10) === 0 || i === series.length - 1) && (
                    <text x={cx} y={H - 12} textAnchor="middle" fontSize="10" fill="#00000090">{labelFmt(s.date)}</text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
};

export default AnalyticsSection;
